/**
 * Stable block ids — §5.1's reason the JSON is canonical at all.
 *
 * Anchored comments (§8.1), block-level links (§6.6), and retrieval citations
 * (§9.4) all point at one of these. They are generated once, when a block first
 * appears, and never reassigned: `chunk.block_id` and every comment anchor is a
 * dangling reference the moment an id changes.
 *
 * The generator is wired into `@tiptap/extension-unique-id` in `schema.ts`, so
 * the editor, the collab server, and the markdown parser all mint ids the same
 * way. Phase 1 §2.3 records the licence verification: the extension is MIT and
 * on the public registry, so there is no ProseMirror plugin to hand-roll.
 */

import type { DocumentJSON, NodeJSON } from './types.ts';

/**
 * Which node types carry an id — the `types` option `schema.ts` hands to
 * `UniqueID`, and the set every other module consults.
 *
 * Containers are deliberately absent. An anchor has to be something a reader
 * can point at and a citation can quote; a `bulletList`, a `tableRow`, or a
 * `blockquote` has no text of its own, and its children already carry anchors.
 * The second reason is mechanical: every type here has a natural slot in
 * markdown for the marker, and a type without one could only round-trip by
 * inventing syntax — which is how a dialect stops being markdown.
 */
export const ID_BEARING_TYPES: ReadonlySet<string> = new Set([
  'paragraph',
  'heading',
  'codeBlock',
  'image',
  'table',
  'callout',
  'embed',
  'liveData',
]);

/**
 * Crockford's base32 without `i`, `l`, `o`, or `u` — twelve characters, sixty
 * bits.
 *
 * Not UUIDv7, which §8.1 makes the format for every *table* key. A block id is
 * not a row key; it is a token that appears inline in the derived markdown on
 * every id-bearing block, and thirty-six characters of it per paragraph would
 * drown the readable line diffs that are half of ADR 0002's argument. Sixty
 * bits is far past collision risk in a corpus of low thousands of documents,
 * and the excluded letters keep an id legible when someone is comparing a
 * citation against a document by eye.
 */
const ALPHABET = '0123456789abcdefghjkmnpqrstvwxyz';

export const BLOCK_ID_LENGTH = 12;

/** What this generator produces, as distinct from what the parser accepts. */
export const BLOCK_ID_PATTERN = /^[0-9abcdefghjkmnpqrstvwxyz]{12}$/;

export function generateBlockId(): string {
  const bytes = new Uint8Array(BLOCK_ID_LENGTH);
  crypto.getRandomValues(bytes);
  let out = '';
  for (const byte of bytes) {
    out += ALPHABET[byte & 31] as string;
  }
  return out;
}

/**
 * Fills in an id on every id-bearing block that lacks one, returning a new
 * document; the input is not mutated.
 *
 * This is what closes the §11.3 hole. An agent writing markdown back has no
 * reason to invent block ids, so the parser produces blocks without them — and
 * a block without an id cannot be commented on, linked to, or cited. The write
 * path runs this before storing, which is why `deriveDocumentContent` calls it
 * rather than leaving every caller to remember.
 */
export function ensureBlockIds(doc: DocumentJSON): DocumentJSON {
  return walk(doc) as DocumentJSON;
}

function walk(node: NodeJSON): NodeJSON {
  const content = node.content?.map(walk);
  const needsId = ID_BEARING_TYPES.has(node.type) && !node.attrs?.id;
  if (!needsId && !content) return node;
  const next: NodeJSON = { ...node };
  if (content) next.content = content;
  if (needsId) next.attrs = { ...node.attrs, id: generateBlockId() };
  return next;
}

/**
 * Every block id in the document, in document order.
 *
 * The order is the point: §9.4's `chunk.ordinal` and a citation's position in a
 * document are the same sequence, and reading it off the tree keeps the two
 * from being produced by two different walks.
 */
export function collectBlockIds(doc: DocumentJSON): string[] {
  const ids: string[] = [];
  const visit = (node: NodeJSON): void => {
    const id = node.attrs?.id;
    if (ID_BEARING_TYPES.has(node.type) && typeof id === 'string') ids.push(id);
    for (const child of node.content ?? []) visit(child);
  };
  visit(doc);
  return ids;
}
